import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { API } from "@/lib/api";

export default function Concierge() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Welcome to PLAYA. I'm your concierge — how may I curate today?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", text: userMsg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n"); buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const chunk = line.slice(6);
          if (chunk === "[DONE]") continue;
          if (chunk.startsWith("[ERROR]")) { setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: chunk }; return c; }); continue; }
          setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + chunk }; return c; });
        }
      }
    } catch { setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "The concierge is momentarily unavailable." }; return c; }); }
    finally { setBusy(false); }
  };

  const renderMd = (t) => t.split(/(\[[^\]]+\]\([^)]+\))/g).map((seg, i) => {
    const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(seg);
    if (m) return <a key={i} href={m[2]} className="text-[#D4AF37] underline">{m[1]}</a>;
    return <span key={i}>{seg}</span>;
  });

  return (
    <>
      <button data-testid="concierge-open" onClick={() => setOpen(!open)} className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-2xl hover:scale-105 transition">
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[380px] max-w-[calc(100vw-3rem)] h-[520px] glass rounded-2xl flex flex-col overflow-hidden border border-white/10">
          <div className="p-4 border-b border-white/10 flex items-center gap-2">
            <Sparkles size={16} className="text-[#D4AF37]" />
            <div className="font-display text-sm">PLAYA Concierge</div>
            <span className="ml-auto text-[10px] tracking-widest uppercase text-gray-500">Claude Sonnet</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {messages.map((m, i) => (
              <div key={i} data-testid={`concierge-msg-${m.role}`} className={m.role === "user" ? "text-right" : ""}>
                <div className={`inline-block px-3 py-2 rounded-lg max-w-[85%] ${m.role === "user" ? "bg-[#D4AF37]/15 text-white" : "bg-white/5 text-gray-200"}`}>
                  {renderMd(m.text || (busy && i === messages.length - 1 ? "…" : ""))}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input data-testid="concierge-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask for a recommendation…" />
            <button data-testid="concierge-send" onClick={send} disabled={busy} className="btn-gold px-4 py-2"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
